/**
 * MemberRow - Single row in the member list.
 * Desktop: hover reveals "變更角色"/"移除" buttons (design: tripways.pen node UWQWT, Member Row 3 - Actions Visible)
 * Mobile: swipe left reveals the same two actions as colored blocks (design: node PAQFj, Member Row 3 - Swiped)
 *
 * Only the owner sees actions, and never on their own row (design: "你" tag, node exS0g).
 */

import { useRef, useState } from 'react';
import { Shield, UserMinus } from 'lucide-react';
import { formatDate } from '../../utils/dates';
import { getAvatarFallback } from '../../hooks/useUserProfiles';
import type { MemberWithProfile } from '../../hooks/useTripMembers';
import type { MemberRole } from '../../types/member';

const FALLBACK_COLORS = ['#0dccf2', '#f59e0b', '#8b5cf6', '#10b981', '#ef4444', '#3b82f6'];
function colorForUserId(userId: number): string {
  return FALLBACK_COLORS[userId % FALLBACK_COLORS.length];
}

const SWIPE_REVEAL_WIDTH = 152; // 80 (Change Role) + 72 (Remove)
const SWIPE_THRESHOLD = 60;

interface MemberRowProps {
  member: MemberWithProfile;
  myRole: MemberRole;
  currentUserId: number;
  onChangeRole: () => void;
  onRemove: () => void;
}

export default function MemberRow({ member, myRole, currentUserId, onChangeRole, onRemove }: MemberRowProps) {
  const isSelf = member.userId === currentUserId;
  const canManage = myRole === 'OWNER' && !isSelf;

  const [swiped, setSwiped] = useState(false);
  const touchStartX = useRef<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!canManage) return;
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!canManage || touchStartX.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (deltaX < -SWIPE_THRESHOLD) setSwiped(true);
    else if (deltaX > SWIPE_THRESHOLD) setSwiped(false);
  };

  const avatar = (
    <div
      className="flex items-center justify-center rounded-full text-white font-bold shrink-0 overflow-hidden"
      style={{ width: 40, height: 40, backgroundColor: colorForUserId(member.userId) }}
    >
      {member.avatar ? (
        <img src={member.avatar} alt={member.nickname} className="w-full h-full object-cover" />
      ) : (
        getAvatarFallback(member.nickname)
      )}
    </div>
  );

  const roleTag =
    member.role === 'OWNER' ? (
      <span className="text-[11px] font-semibold text-primary bg-primary/10 rounded-[10px] px-2 py-[3px]">
        擁有者
      </span>
    ) : (
      <span className="text-xs font-medium text-slate-400 bg-slate-100 rounded-[10px] px-2 py-[3px]">
        編輯者
      </span>
    );

  return (
    <div className="group relative overflow-hidden border-b border-slate-100 last:border-b-0">
      {/* ── Desktop ── */}
      <div className="hidden lg:flex items-center justify-between px-6 py-5">
        <div className="flex items-center gap-4">
          {avatar}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1.5">
              <span className="text-[15px] font-semibold text-slate-900">{member.nickname}</span>
              {isSelf && (
                <span className="text-[11px] font-semibold text-primary bg-primary/10 rounded-lg px-1.5 py-0.5">
                  你
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {roleTag}
              <span className="text-xs text-slate-400">{formatDate(new Date(member.joinedAt), 'yyyy/MM')} 加入</span>
            </div>
          </div>
        </div>

        {canManage && (
          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={onChangeRole}
              className="flex items-center gap-1.5 h-8 px-3 rounded-lg border border-slate-200 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <Shield size={14} />
              變更角色
            </button>
            <button
              onClick={onRemove}
              className="flex items-center gap-1.5 h-8 px-3 rounded-lg bg-red-100 text-xs font-medium text-red-600 hover:bg-red-200 transition-colors"
            >
              <UserMinus size={14} />
              移除
            </button>
          </div>
        )}
      </div>

      {/* ── Mobile ── */}
      <div className="lg:hidden relative">
        <div
          className="flex items-center justify-between px-5 py-4 bg-white transition-transform duration-200"
          style={{ transform: swiped ? `translateX(-${SWIPE_REVEAL_WIDTH}px)` : 'translateX(0)' }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div className="flex items-center gap-3">
            <div
              className="flex items-center justify-center rounded-full text-white font-bold shrink-0 overflow-hidden"
              style={{ width: 44, height: 44, backgroundColor: colorForUserId(member.userId) }}
            >
              {member.avatar ? (
                <img src={member.avatar} alt={member.nickname} className="w-full h-full object-cover" />
              ) : (
                getAvatarFallback(member.nickname)
              )}
            </div>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1.5">
                <span className="text-[15px] font-semibold text-slate-900">{member.nickname}</span>
                {isSelf && (
                  <span className="text-[11px] font-semibold text-primary bg-primary/10 rounded-lg px-1.5 py-0.5">
                    你
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {roleTag}
                <span className="text-xs text-slate-400">
                  {formatDate(new Date(member.joinedAt), 'yyyy/MM')} 加入
                </span>
              </div>
            </div>
          </div>
        </div>

        {canManage && (
          <div className="absolute inset-y-0 right-0 flex" style={{ width: SWIPE_REVEAL_WIDTH }}>
            <button
              onClick={() => {
                setSwiped(false);
                onChangeRole();
              }}
              className="flex flex-col items-center justify-center gap-1 bg-amber-500 text-white text-[10px] font-semibold"
              style={{ width: 80 }}
            >
              <Shield size={18} />
              變更角色
            </button>
            <button
              onClick={() => {
                setSwiped(false);
                onRemove();
              }}
              className="flex flex-col items-center justify-center gap-1 bg-red-500 text-white text-[10px] font-semibold"
              style={{ width: 72 }}
            >
              <UserMinus size={18} />
              移除
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
